import { useEffect, useRef } from "react";

function shouldDeferEnterToFocusedControl(el: Element | null): boolean {
  if (!el || !(el instanceof HTMLElement)) {
    return false;
  }
  if (el.tagName === "BUTTON") {
    return true;
  }
  if (el.getAttribute("role") === "button") {
    return true;
  }
  if (el.tagName === "TEXTAREA") {
    return true;
  }
  if (el.isContentEditable) {
    return true;
  }
  if (el.tagName === "SELECT") {
    return true;
  }
  if (el.tagName === "INPUT") {
    const t = (el as HTMLInputElement).type?.toLowerCase() ?? "text";
    if (
      ["checkbox", "radio", "file", "button", "submit", "reset", "search"].includes(t)
    ) {
      return true;
    }
  }
  return false;
}

function isActiveElementInsideModalSurface(): boolean {
  const el = document.activeElement;
  if (!el || !(el instanceof Element)) {
    return false;
  }
  return (
    el.closest('[role="dialog"],[role="alertdialog"]') !== null
  );
}

export type UseEnterKeyModalDefaultActionOptions = {
  /** When true, Enter does not invoke the default action (e.g. primary button disabled). */
  primaryDisabled?: boolean;
};

/**
 * While a modal is open, Enter invokes the primary/default action when focus is inside
 * a dialog surface and is not on a control that already defines Enter behavior.
 */
export function useEnterKeyModalDefaultAction(
  isOpen: boolean,
  onDefaultAction: () => void,
  options?: UseEnterKeyModalDefaultActionOptions
): void {
  const { primaryDisabled = false } = options ?? {};
  const onDefaultActionRef = useRef(onDefaultAction);
  onDefaultActionRef.current = onDefaultAction;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.repeat) {
        return;
      }
      if (primaryDisabled) {
        return;
      }
      if (!isActiveElementInsideModalSurface()) {
        return;
      }
      if (shouldDeferEnterToFocusedControl(document.activeElement)) {
        return;
      }
      e.preventDefault();
      onDefaultActionRef.current();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, primaryDisabled]);
}
